import { Observable } from 'rxjs';
import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { EMAIL_VALIDATOR_PATTERN, PASSWORD_VALIDATOR_PATTERN } from '@core/constants/FomPatterns';
import { NavigationService } from '@shared/services/navigation.service';
import { ExternalLinksService } from '@shared/services/external-links.service';
import { Language } from '@core/modules/language/models';
import { RegisterFormControlName } from '@modules/public/register/components/register-form/models/register-form-control-name.enum';
import { BaseOption } from '@shared/models/base-option';
import { selectCurrentLanguage } from '@core/modules/language/features/language.slice';
import { selectCountries } from '@core/modules/countries/features/countries.slice';

type RegisterFormControls = {
  [RegisterFormControlName.EMAIL]: FormControl<string | null>;
  [RegisterFormControlName.COUNTRY]: FormControl<number | null>;
  [RegisterFormControlName.PASSWORD]: FormControl<string | null>;
  [RegisterFormControlName.REPEAT_PASSWORD]: FormControl<string | null>;
  [RegisterFormControlName.TERM_CONDITIONS]: FormControl<boolean>;
  [RegisterFormControlName.RECEIVE_PROMOTIONS]: FormControl<boolean>;
};

type RegisterFormGroup = FormGroup<RegisterFormControls>;

@Component({
    selector: 'app-register-form',
    templateUrl: './register-form.component.html',
    styleUrls: ['./register-form.component.scss'],
    standalone: false
})
export class RegisterFormComponent implements OnInit {
  @Output() submitForm: EventEmitter<any> = new EventEmitter<any>();

  form!: RegisterFormGroup;
  errorFeedbackList = this.loadFeedbackErrorLists();

  currentLanguage$!: Observable<Language | undefined>;
  countries$!: Observable<BaseOption[]>;

  registerFormControlName = RegisterFormControlName;

  constructor(
    private readonly store: Store,
    private readonly fb: FormBuilder,
    private readonly navigationService: NavigationService,
    private readonly externalLinksService: ExternalLinksService
  ) {}

  get emailControl() {
    return <RegisterFormControls[RegisterFormControlName.EMAIL]>(
      this.form.get(RegisterFormControlName.EMAIL)
    );
  }

  get countryControl() {
    return <RegisterFormControls[RegisterFormControlName.COUNTRY]>(
      this.form.get(RegisterFormControlName.COUNTRY)
    );
  }

  get passwordControl() {
    return <RegisterFormControls[RegisterFormControlName.PASSWORD]>(
      this.form.get(RegisterFormControlName.PASSWORD)
    );
  }

  get repeatPasswordControl() {
    return <RegisterFormControls[RegisterFormControlName.REPEAT_PASSWORD]>(
      this.form.get(RegisterFormControlName.REPEAT_PASSWORD)
    );
  }

  ngOnInit(): void {
    this.currentLanguage$ = this.store.select(selectCurrentLanguage);
    this.countries$ = this.store.select(selectCountries);

    this.form = this.initForm();
    this.errorFeedbackList = this.loadFeedbackErrorLists();
  }

  onOpenLink(event: MouseEvent, url: string): void {
    this.navigationService.openLink(url);
    event.stopPropagation();
  }

  onSubmit() {
    this.form.valid && this.submitForm.emit(this.form.value);
  }

  isSubmitted(control: AbstractControl): boolean {
    return !control.pristine;
  }

  checkPasswords(): ValidatorFn {
    return (control: AbstractControl) =>
      control.parent?.get(RegisterFormControlName.PASSWORD)?.value === control.value
        ? null
        : { notSame: true };
  }

  getTermOfUseUrl(locale: string) {
    return this.externalLinksService.getTermOfUseLink(locale);
  }

  getPrivacyPolicyUrl(locale: string) {
    return this.externalLinksService.getPrivacyPolicyLink(locale);
  }

  private initForm() {
    return <RegisterFormGroup>this.fb.group({
      [RegisterFormControlName.EMAIL]: [
        '',
        [Validators.required, Validators.pattern(EMAIL_VALIDATOR_PATTERN)]
      ],
      [RegisterFormControlName.COUNTRY]: [null, [Validators.required]],
      [RegisterFormControlName.PASSWORD]: [
        '',
        [Validators.required, Validators.pattern(PASSWORD_VALIDATOR_PATTERN)]
      ],
      [RegisterFormControlName.REPEAT_PASSWORD]: ['', [Validators.required, this.checkPasswords()]],
      [RegisterFormControlName.TERM_CONDITIONS]: [false, [Validators.requiredTrue]],
      [RegisterFormControlName.RECEIVE_PROMOTIONS]: [false]
    });
  }

  private loadFeedbackErrorLists() {
    return {
      [RegisterFormControlName.EMAIL]: {
        required: 'form.errors.required',
        pattern: 'form.errors.emailLoginPattern'
      },
      [RegisterFormControlName.COUNTRY]: {
        required: 'form.errors.required'
      },
      [RegisterFormControlName.PASSWORD]: {
        required: 'form.errors.required',
        pattern: 'form.errors.pattern'
      },
      [RegisterFormControlName.REPEAT_PASSWORD]: {
        required: 'form.errors.required',
        notSame: 'form.errors.notSame'
      },
      [RegisterFormControlName.TERM_CONDITIONS]: {
        required: 'form.errors.required'
      }
    };
  }
}
